/**
 * QuickModeChat Component
 *
 * Chat interface for Quick Mode natural language input
 * Displays chat history, tier classification, and upgrade button
 */

import React, { useState, useRef, useEffect } from 'react';
import type { QuickModeChatProps, ChatMessage } from '../types/forge';
import styles from '../styles/forge.module.css';

const TIER_LABELS = {
  quick: 'Quick',
  standard: 'Standard',
  complex: 'Complex',
};

export const QuickModeChat: React.FC<QuickModeChatProps> = ({
  onSubmit,
  isSubmitting,
  requirement,
  error,
}) => {
  const [message, setMessage] = useState<string>('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  // Scroll to bottom when chat history changes
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  // Add requirement to chat history when submitted
  useEffect(() => {
    if (requirement && chatHistory.length === 0) {
      setChatHistory([
        {
          id: '1',
          role: 'user',
          content: requirement.description,
          timestamp: requirement.createdAt,
        },
        {
          id: '2',
          role: 'assistant',
          content: `I've analyzed your request and classified it as **${TIER_LABELS[requirement.tier]}** tier.`,
          timestamp: requirement.createdAt,
          tier: requirement.tier,
        },
      ]);
    }
  }, [requirement, chatHistory]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim() || isSubmitting) {
      return;
    }

    const userMessage: ChatMessage = {
      id: `${Date.now()}-user`,
      role: 'user',
      content: message.trim(),
      timestamp: new Date().toISOString(),
    };

    setChatHistory((prev) => [...prev, userMessage]);
    setMessage('');

    try {
      await onSubmit(message.trim());
    } catch (err) {
      // Error is handled by parent component
      console.error('Failed to submit message:', err);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const showUpgradeButton =
    requirement &&
    (requirement.tier === 'standard' || requirement.tier === 'complex') &&
    requirement.status === 'draft';

  return (
    <div className={styles.quickModeChat}>
      <div className={styles.chatContainer} ref={chatContainerRef}>
        {chatHistory.length === 0 ? (
          <div className={styles.chatPlaceholder}>
            <p>Describe what you'd like to build or fix...</p>
            <p className={styles.chatPlaceholderHint}>
              Examples:
              <br />
              • "Fix the date picker on trade entry form"
              <br />
              • "Add export to Excel button for yield curves"
              <br />
              • "Improve error handling in pricing service"
            </p>
          </div>
        ) : (
          <div className={styles.chatHistory}>
            {chatHistory.map((msg) => (
              <div
                key={msg.id}
                className={`${styles.chatMessage} ${
                  msg.role === 'user' ? styles.chatMessageUser : styles.chatMessageAssistant
                }`}
              >
                <div className={styles.chatMessageContent}>
                  {msg.content}
                  {msg.tier && (
                    <span className={`${styles.badge} ${styles[`badge--${msg.tier}`]} ${styles.chatMessageBadge}`}>
                      {TIER_LABELS[msg.tier]}
                    </span>
                  )}
                </div>
                <div className={styles.chatMessageTime}>
                  {new Date(msg.timestamp).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className={styles.chatError}>
            <p>Error: {error}</p>
          </div>
        )}
      </div>

      {showUpgradeButton && (
        <div className={styles.chatUpgradeNotice}>
          <p>
            This request is classified as <strong>{TIER_LABELS[requirement.tier]}</strong> tier and
            may require more details.
          </p>
          <button
            className={`${styles.button} ${styles.buttonSecondary}`}
            onClick={() => {
              // Mode switch will be handled by parent component
              console.log('Upgrade to Detailed Mode');
            }}
          >
            Switch to Detailed Mode
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.chatInputForm}>
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message... (Shift+Enter for new line)"
          className={styles.chatInput}
          disabled={isSubmitting}
          rows={1}
          aria-label="Message input"
        />
        <button
          type="submit"
          className={`${styles.button} ${styles.buttonPrimary} ${styles.chatSubmitButton}`}
          disabled={!message.trim() || isSubmitting}
          aria-label="Send message"
        >
          {isSubmitting ? 'Sending...' : 'Send'}
        </button>
      </form>
    </div>
  );
};

export default QuickModeChat;
